import { Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TextAreaComponent } from '../../../common/text-area/text-area.component';
import { Subscription } from 'rxjs';
import { CreateErrorCodeDto } from '../../../../../shared/models/error-code/create-error-code-dto';
import { ErrorCodeApiService } from '../../../../services/api/error-code-api.service';
import { SelectionOptionValue } from '../../../../../shared/models/internal/selection-option-value';
import { SelectionComponent } from '../../../common/selection/selection.component';
import { NwNumberApiService } from '../../../../services/api/nw-number-api.service';
import { minSelectedValidator } from '../../../../../shared/validators/min-selected.validator';
import { errorCodeCodeDefaultPlaceholer, errorCodeCodeRegExValidator } from '../../../../common/error-code-validation';
import { FloppyIconComponent } from '../../../icons/floppy-icon/floppy-icon.component';
import { LabelWithHelp } from '../../../common/label-with-help/label-with-help';
import { errorToString } from '../../../../common/common-utils';
import { AgentActionDirective } from '../../../../directives/agent-action.directive';
import { HotToastService } from '@ngxpert/hot-toast';

@Component({
  selector: 'app-new-error-code',
  imports: [
    FormsModule,
    SelectionComponent,
    CommonModule,
    TextAreaComponent,
    ReactiveFormsModule,
    FloppyIconComponent,
    LabelWithHelp,
    AgentActionDirective,
  ],
  templateUrl: './new-error-code.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './new-error-code.component.scss',
})
export class NewErrorCodeComponent implements OnInit, OnDestroy {
  public newErrorCode: CreateErrorCodeDto = {} as CreateErrorCodeDto;
  public form!: FormGroup;
  public internalNotes!: FormControl;
  public code!: FormControl;
  public message!: FormControl;
  public startingWithVersion!: FormControl;
  public endingWithVersion!: FormControl;
  public causeIntern!: FormControl;
  public causeDealer!: FormControl;
  public causeFarmer!: FormControl;
  public remedyFarmer!: FormControl;
  public remedyDealer!: FormControl;
  public remedyIntern!: FormControl;
  public machineType!: FormControl;
  public needsReview!: FormControl;
  public nwNumbers!: FormControl;
  public nwNumberValues: SelectionOptionValue[] = [];
  public fitnessExclude!: FormControl;
  public aggregateMessage!: FormControl;
  public category!: FormControl;
  public errorCodeCategories: SelectionOptionValue[] = [{ id: '-1', selected: true, title: 'Select a category' }];
  public errorCodeCodePlaceholder = errorCodeCodeDefaultPlaceholer;

  private errorCodeApi = inject(ErrorCodeApiService);
  private nwNumberApi = inject(NwNumberApiService);
  private toast = inject(HotToastService);
  private formSubscription?: Subscription;

  public ngOnInit(): void {
    this.fetchErrorCodeCategories();
    this.fetchNwNumbers();
    this.buildForm();
    this.unsubscribeForm();
  }

  public ngOnDestroy(): void {
    this.unsubscribeForm();
  }

  public createErrorCode(): void {
    this.prepareCreateErrorCode();
    this.errorCodeApi.createErrorCode(this.newErrorCode).subscribe({
      next: (errorCode) => {
        this.toast.success(`Created successfully ErrorCode ${errorCode.code}`);
        this.form.reset();
      },
      error: (error) => {
        console.error(errorToString(error));
      },
    });
  }

  private prepareCreateErrorCode(): void {
    this.newErrorCode.code = this.form.get('code')?.value;
    this.newErrorCode.message = this.form.get('message')?.value;
    this.newErrorCode.startingWithVersion = this.form.get('startingWithVersion')?.value;
    this.newErrorCode.endingWithVersion = this.form.get('endingWithVersion')?.value;
    this.newErrorCode.causeIntern = this.form.get('causeIntern')?.value;
    this.newErrorCode.causeDealer = this.form.get('causeDealer')?.value;
    this.newErrorCode.causeFarmer = this.form.get('causeFarmer')?.value;
    this.newErrorCode.remedyFarmer = this.form.get('remedyFarmer')?.value;
    this.newErrorCode.remedyDealer = this.form.get('remedyDealer')?.value;
    this.newErrorCode.remedyIntern = this.form.get('remedyIntern')?.value;
    this.newErrorCode.machineType = this.form.get('machineType')?.value;
    this.newErrorCode.needsReview = this.form.get('needsReview')?.value ?? false;
    this.newErrorCode.fitnessExclude = this.form.get('fitnessExclude')?.value ?? false;
    this.newErrorCode.aggregateMessage = this.form.get('aggregateMessage')?.value ?? false;
    this.newErrorCode.category = this.form.get('category')?.value;
    this.newErrorCode.nwNumberIds = this.form.get('nwNumbers')?.value;

    this.newErrorCode.internalNotes = this.internalNotes.value === '' ? null : this.internalNotes.value;
  }

  private buildForm(): void {
    this.code = new FormControl(null, [Validators.required, errorCodeCodeRegExValidator]);
    this.message = new FormControl(null, []);
    this.startingWithVersion = new FormControl(null, []);
    this.endingWithVersion = new FormControl(null, []);
    this.causeIntern = new FormControl(null, []);
    this.causeDealer = new FormControl(null, []);
    this.causeFarmer = new FormControl(null, []);
    this.remedyFarmer = new FormControl(null, []);
    this.remedyDealer = new FormControl(null, []);
    this.remedyIntern = new FormControl(null, []);
    this.machineType = new FormControl(null, []);
    this.needsReview = new FormControl(null, []);
    this.fitnessExclude = new FormControl(null, []);
    this.aggregateMessage = new FormControl(null, []);
    this.internalNotes = new FormControl(null, []);
    this.nwNumbers = new FormControl(null, [minSelectedValidator(1)]);
    this.category = new FormControl(this.errorCodeCategories[0].id, []);

    this.form = new FormGroup({
      code: this.code,
      message: this.message,
      startingWithVersion: this.startingWithVersion,
      endingWithVersion: this.endingWithVersion,
      causeIntern: this.causeIntern,
      causeDealer: this.causeDealer,
      causeFarmer: this.causeFarmer,
      remedyFarmer: this.remedyFarmer,
      remedyDealer: this.remedyDealer,
      remedyIntern: this.remedyIntern,
      machineType: this.machineType,
      needsReview: this.needsReview,
      fitnessExclude: this.fitnessExclude,
      aggregateMessage: this.aggregateMessage,
      internalNotes: this.internalNotes,
      category: this.category,
      nwNumbers: this.nwNumbers,
    });
  }

  private fetchNwNumbers(): void {
    this.nwNumberApi.getAllNwNumbers().subscribe({
      next: (nwNumbers) => {
        this.nwNumberValues = [
          ...this.nwNumberValues,
          ...nwNumbers.map((nwNumber) => {
            return { id: nwNumber.id!, selected: false, title: nwNumber.number! };
          }),
        ];
      },
      error: (error) => {
        console.error(errorToString(error));
      },
    });
  }

  private fetchErrorCodeCategories(): void {
    this.errorCodeApi.getErrorCodeCategories().subscribe({
      next: (errorCodeCategories) => {
        this.errorCodeCategories = [
          ...this.errorCodeCategories,
          ...errorCodeCategories.map((category) => {
            return { id: category, selected: false, title: category };
          }),
        ];
      },
      error: (error) => {
        console.error(errorToString(error));
      },
    });
  }

  private unsubscribeForm(): void {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }
}
